import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { Navigate, useNavigate } from "react-router-dom";
import { getAllUsers, UserWithArtists, PaginationInfo } from "../../services/api/user.service";
import { apiFetch } from "../../services/api/client";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { ChevronLeft, ChevronRight, Filter, X, ChevronUp, ChevronDown, Eye, FileSpreadsheet, FileText, CheckCircle, Trash2, MoreVertical } from "lucide-react";

// Separate Table Loader Component
const TableLoader: React.FC = () => {
  return (
    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-4 border-gray-300 border-t-gray-800 rounded-full animate-spin"></div>
        <span className="text-sm text-gray-600 font-family-messina-sans">Loading...</span>
      </div>
    </div>
  );
};

const UsersList: React.FC = () => {
  const navigate = useNavigate();
  const [usersData, setUsersData] = useState<UserWithArtists[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  
  // Filter states (for input values)
  const [filters, setFilters] = useState({
    email: "",
    artistName: "",
    fullName: "",
    firstname: "",
    lastname: "",
    mobile: "",
  });
  // Debounced filter values (used for API calls)
  const [debouncedFilters, setDebouncedFilters] = useState({
    email: "",
    artistName: "",
    fullName: "",
    firstname: "",
    lastname: "",
    mobile: "",
  });
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [showExportMenu, setShowExportMenu] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  const [artistNameToDelete, setArtistNameToDelete] = useState<string>("");
  const [deleteConfirmed, setDeleteConfirmed] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
  const [usersToDelete, setUsersToDelete] = useState<Array<{ id: number; artistName: string }>>([]);
  const [selectionMode, setSelectionMode] = useState<boolean>(false);
  const [manageMenuUserId, setManageMenuUserId] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const manageButtonRef = useRef<HTMLButtonElement>(null);
  const [unmapModal, setUnmapModal] = useState<{ user: UserWithArtists; primaryArtist: NonNullable<UserWithArtists["artists"][0]> } | null>(null);
  const [unmapConfirmStep, setUnmapConfirmStep] = useState<1 | 2>(1);
  const [unmapLoading, setUnmapLoading] = useState<boolean>(false);
  const [syncModalUser, setSyncModalUser] = useState<UserWithArtists | null>(null);
  const [syncLoading, setSyncLoading] = useState<boolean>(false);
  const [listRefreshKey, setListRefreshKey] = useState<number>(0);

  // Sorting state
  const [sortConfig, setSortConfig] = useState<{
    key: keyof UserWithArtists | 'artistName' | 'created_at' | 'mobile' | null;
    direction: 'asc' | 'desc';
  }>({
    key: null,
    direction: 'asc',
  });

  // Get systemRole from sessionStorage and check if user has access
  const getSystemRole = (): string | null => {
    try {
      return sessionStorage.getItem('systemRole');
    } catch {
      return null;
    }
  };

  const systemRole = getSystemRole();

  // Normalizes pasted full names so punctuation/copy artifacts don't break filtering
  // Example: "Giovanni," -> "Giovanni"
  const normalizeFullNameTokens = (value: string): string[] => {
    const trimmed = (value || "").trim();
    if (!trimmed) return [];
    return trimmed
      .split(/\s+/)
      .filter(Boolean)
      .map((t) => t.replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, ""))
      .filter(Boolean);
  };

  // Redirect to dashboard if not system admin
  if (systemRole !== "system_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  const isInitialLoad = useRef(true);

  // Debounce filter values (500ms delay)
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 500);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [filters]);

  useEffect(() => {
    const fetchUsersList = async () => {
      try {
        // Use tableLoading only if it's not the initial load
        if (isInitialLoad.current) {
          setLoading(true);
        } else {
          setTableLoading(true);
        }
        setError(null);
        
        // Prepare filter parameters for API using debounced filters
        const filterParams: {
          artist_name?: string;
          email?: string;
          mobile?: string;
          fullName?: string;
          firstname?: string;
          lastname?: string;
        } = {};
        if (debouncedFilters.artistName) filterParams.artist_name = debouncedFilters.artistName;
        if (debouncedFilters.email) filterParams.email = debouncedFilters.email;
        if (debouncedFilters.mobile) filterParams.mobile = debouncedFilters.mobile;

        const tokens = normalizeFullNameTokens(debouncedFilters.fullName || "");
        if (tokens.length > 0) {
          if (tokens.length === 1) {
            filterParams.fullName = tokens[0];
          } else {
            filterParams.firstname = tokens[0];
            filterParams.lastname = tokens.slice(1).join(" ");
          }
        }
        
        const response = await getAllUsers(currentPage, itemsPerPage, false, Object.keys(filterParams).length > 0 ? filterParams : undefined);
        setUsersData(response.data);
        setPagination(response.pagination);
      } catch (err: any) {
        console.error("Error fetching users list:", err);
        setError(err.message || "Failed to fetch users list");
        toast.error(err.message || "Failed to fetch users list");
      } finally {
        if (isInitialLoad.current) {
          setLoading(false);
          isInitialLoad.current = false;
        } else {
          
          setTableLoading(false);
        }
      }
    };
    fetchUsersList();
  }, [
    currentPage,
    itemsPerPage,
    debouncedFilters.artistName,
    debouncedFilters.email,
    debouncedFilters.mobile,
    debouncedFilters.fullName,
    debouncedFilters.firstname,
    debouncedFilters.lastname,
    listRefreshKey,
  ]);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showExportMenu && !target.closest('.export-menu-container')) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportMenu]);

  // Close manage menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (manageMenuUserId !== null && !target.closest('[data-manage-menu]') && !target.closest('[data-manage-dropdown]')) {
        setManageMenuUserId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [manageMenuUserId]);

  // Position the manage dropdown (portal) from the trigger button
  useLayoutEffect(() => {
    if (manageMenuUserId == null) {
      setMenuPosition(null);
      return;
    }
    const el = manageButtonRef.current;
    if (!el) {
      setMenuPosition(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    const dropdownWidth = 160;
    const dropdownHeightEstimate = 140;
    const padding = 8;
    let left = rect.right - dropdownWidth;
    let top: number;
    if (rect.bottom + dropdownHeightEstimate + padding > window.innerHeight - padding) {
      top = rect.top - dropdownHeightEstimate - padding;
    } else {
      top = rect.bottom + padding;
    }
    if (top < padding) top = padding;
    if (top + dropdownHeightEstimate > window.innerHeight - padding) top = window.innerHeight - dropdownHeightEstimate - padding;
    if (left < padding) left = padding;
    if (left + dropdownWidth > window.innerWidth - padding) left = window.innerWidth - dropdownWidth - padding;
    setMenuPosition({ top, left });
  }, [manageMenuUserId]);

  // Sort users data (filtering is handled server-side)
  const filteredAndSortedUsersData = useMemo(() => {
    // Data is already filtered by the server, just apply sorting
    let sorted = [...usersData];

    // Apply sorting
    if (sortConfig.key) {
      sorted = [...sorted].sort((a, b) => {
        let aValue: any;
        let bValue: any;

        if (sortConfig.key === 'artistName') {
          const aArtist = a.artists?.find(artist => artist.is_primary) || a.artists?.[0];
          const bArtist = b.artists?.find(artist => artist.is_primary) || b.artists?.[0];
          aValue = aArtist?.artist_name || "";
          bValue = bArtist?.artist_name || "";
        } else if (sortConfig.key === 'created_at') {
          // Handle date sorting
          aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
          bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        } else {
          aValue = a[sortConfig.key as keyof UserWithArtists];
          bValue = b[sortConfig.key as keyof UserWithArtists];
        }

        // Handle null/undefined values
        if (aValue == null) aValue = "";
        if (bValue == null) bValue = "";

        // Compare values
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        // String comparison
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();

        if (aStr < bStr) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aStr > bStr) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return sorted;
  }, [usersData, sortConfig]);

  // Use server-side pagination info
  const totalPages = pagination?.totalPages || 0;
  const totalUsers = pagination?.total || 0;
  const currentPageData = filteredAndSortedUsersData;
  const manageUser = manageMenuUserId != null ? currentPageData.find((u) => u.id === manageMenuUserId) : null;

  // Reset to page 1 when debounced filters change (will trigger new fetch)
  useEffect(() => {
    if (!isInitialLoad.current) {
      setCurrentPage(1);
    }
  }, [debouncedFilters.artistName, debouncedFilters.email, debouncedFilters.mobile]);

  const handlePageChange = (page: number) => {
    if (pagination && page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
      // Scroll to top of table
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSort = (key: keyof UserWithArtists | 'artistName' | 'created_at' | 'mobile') => {
    setSortConfig((prevConfig) => {
      // If clicking the same column, toggle direction
      if (prevConfig.key === key) {
        return {
          key,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      // If clicking a different column, set it to ascending
      return {
        key,
        direction: 'asc',
      };
    });
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page
      pages.push(1);

      // Calculate start and end of visible pages
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if we're near the start
      if (currentPage <= 3) {
        start = 2;
        end = 4;
      }

      // Adjust if we're near the end
      if (currentPage >= totalPages - 2) {
        start = totalPages - 3;
        end = totalPages - 1;
      }

      // Add ellipsis after first page if needed
      if (start > 2) {
        pages.push('...');
      }

      // Add visible pages
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add ellipsis before last page if needed
      if (end < totalPages - 1) {
        pages.push('...');
      }

      // Show last page
      pages.push(totalPages);
    }

    return pages;
  };

  // Fetch all data based on filters (ignoring pagination)
  const fetchAllFilteredData = async (): Promise<UserWithArtists[]> => {
    try {
      // Prepare filter parameters for API using debounced filters
      const filterParams: {
        artist_name?: string;
        email?: string;
        mobile?: string;
        fullName?: string;
        firstname?: string;
        lastname?: string;
      } = {};
      if (debouncedFilters.artistName) filterParams.artist_name = debouncedFilters.artistName;
      if (debouncedFilters.email) filterParams.email = debouncedFilters.email;
      if (debouncedFilters.mobile) filterParams.mobile = debouncedFilters.mobile;

        const tokens = normalizeFullNameTokens(debouncedFilters.fullName || "");
        if (tokens.length > 0) {
          if (tokens.length === 1) {
            filterParams.fullName = tokens[0];
          } else {
            filterParams.firstname = tokens[0];
            filterParams.lastname = tokens.slice(1).join(" ");
          }
        } else {
          // Shouldn't normally happen after UI changes, but keep backward compatibility
          if (debouncedFilters.firstname) filterParams.firstname = debouncedFilters.firstname;
          if (debouncedFilters.lastname) filterParams.lastname = debouncedFilters.lastname;
        }
      
      // Single API call with export=true to get all filtered data
      const response = await getAllUsers(1, 1, true, Object.keys(filterParams).length > 0 ? filterParams : undefined);
      return response.data;
    } catch (error) {
      console.error("Error fetching all data for export:", error);
      throw error;
    }
  };

  // Prepare export data (shared between CSV and Excel)
  const prepareExportData = async () => {
    // Fetch all filtered data (ignoring pagination)
    const allFilteredData = await fetchAllFilteredData();
    
    return allFilteredData.map((user) => {
      const primaryArtist = user.artists?.find(artist => artist.is_primary) || user.artists?.[0];
      const artistName = primaryArtist?.artist_name || "N/A";
      const isMapped = !!primaryArtist?.is_artist_mapped;
      
      return {
        "Registered Date": formatDate(user.created_at),
        "Artist Name": artistName,
        "Email": user.email || "N/A",
        "Full Name": `${user.firstname || ''} ${user.lastname || ''}`.trim() || "N/A",
        "First Name": user.firstname || "N/A",
        "Last Name": user.lastname || "N/A",
        "Mobile": user.mobile || "N/A",
        "Address": user.address || "N/A",
        "State": user.state || "N/A",
        "City": user.city || "N/A",
        "Country": user.country || "N/A",
        "Street": user.street || "N/A",
        "Zip Code": user.zip_code || "N/A",
        "Chartmetric ID": primaryArtist?.chartmetric_id || "N/A",
        "Artist Sync Status": isMapped ? "synced" : "not synced"
      };
    });
  };

  const exportToExcel = async () => {
    try {
      toast.info("Preparing export data...");
      const exportData = await prepareExportData();
      
      if (exportData.length === 0) {
        toast.error("No data to export");
        return;
      }

      // Create a new workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Users List");

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      
      // Generate filename with current date
      const date = new Date();
      const dateStr = date.toISOString().split("T")[0];
      link.download = `users_list_${dateStr}.xlsx`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setShowExportMenu(false);
      toast.success("Excel file downloaded successfully");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export Excel file");
    }
  };

  const exportToCSV = async () => {
    try {
      toast.info("Preparing export data...");
      const exportData = await prepareExportData();
      
      if (exportData.length === 0) {
        toast.error("No data to export");
        return;
      }

      // Convert to CSV format
      const headers = Object.keys(exportData[0]);
      const csvRows = [
        headers.join(','), // Header row
        ...exportData.map(row => 
          headers.map(header => {
            const value = row[header as keyof typeof row];
            // Escape commas and quotes in CSV
            if (value === null || value === undefined) return '';
            const stringValue = String(value);
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          }).join(',')
        )
      ];

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      
      // Generate filename with current date
      const date = new Date();
      const dateStr = date.toISOString().split("T")[0];
      link.download = `users_list_${dateStr}.csv`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setShowExportMenu(false);
      toast.success("CSV file downloaded successfully");
    } catch (error) {
      console.error("Error exporting to CSV:", error);
      toast.error("Failed to export CSV file");
    }
  };

  // Export a CSV of artists with: Artist Id (same value sent in the Nebula JWT),
  // Name (artist name), and Spotify link (blank when missing). One row per artist.
  const exportArtistsSpotifyCSV = async () => {
    try {
      toast.info("Preparing export data...");
      const allFilteredData = await fetchAllFilteredData();

      // Rows created by the sync job use a placeholder Spotify URL; treat it as empty.
      const DUMMY_SPOTIFY_URL = "https://dummy.spotify.com";

      const rows: Array<{ "Artist Id": number; Name: string; "Spotify Link": string }> = [];
      allFilteredData.forEach((user) => {
        (user.artists || []).forEach((artist) => {
          if (!artist?.id) return;
          const spotify =
            artist.spotify_url && artist.spotify_url !== DUMMY_SPOTIFY_URL
              ? artist.spotify_url
              : "";
          rows.push({
            "Artist Id": artist.id,
            Name: artist.artist_name || "",
            "Spotify Link": spotify,
          });
        });
      });

      if (rows.length === 0) {
        toast.error("No data to export");
        return;
      }

      const headers = ["Artist Id", "Name", "Spotify Link"];
      const esc = (value: unknown) => {
        if (value === null || value === undefined) return "";
        let s = String(value);
        // Neutralize CSV/spreadsheet formula injection from user-supplied names.
        if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
        if (s.includes(",") || s.includes('"') || s.includes("\n")) {
          return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
      };

      const csvContent = [
        headers.join(","),
        ...rows.map((row) => headers.map((h) => esc(row[h as keyof typeof row])).join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const dateStr = new Date().toISOString().split("T")[0];
      link.download = `artist_spotify_${dateStr}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setShowExportMenu(false);
      toast.success("Artist + Spotify CSV downloaded successfully");
    } catch (error) {
      console.error("Error exporting artist spotify CSV:", error);
      toast.error("Failed to export CSV file");
    }
  };

  const handleToggleSelectionMode = () => {
    setSelectionMode(prev => {
      if (prev) {
        // Clear selections when disabling selection mode
        setSelectedUsers(new Set());
      }
      return !prev;
    });
  };

  const handleSelectUser = (userId: number, artistName: string) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedUsers.size === currentPageData.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(currentPageData.map(user => user.id)));
    }
  };

  const handleBulkDeleteClick = () => {
    const usersToDeleteList = currentPageData
      .filter(user => selectedUsers.has(user.id))
      .map(user => {
        const primaryArtist = user.artists?.find(artist => artist.is_primary) || user.artists?.[0];
        return {
          id: user.id,
          artistName: primaryArtist?.artist_name || "N/A"
        };
      });
    
    setUsersToDelete(usersToDeleteList);
    setDeleteConfirmed(false);
    setShowDeleteDialog(true);
  };

  const handleDeleteClick = (userId: number, artistName: string) => {
    setUserToDelete(userId);
    setArtistNameToDelete(artistName);
    setUsersToDelete([]);
    setDeleteConfirmed(false);
    setShowDeleteDialog(true);
  };

  const handleRemoveUser = (userId: number) => {
    setUsersToDelete(prev => {
      const updated = prev.filter(user => user.id !== userId);
      // If no users left to delete, close the dialog
      if (updated.length === 0) {
        setShowDeleteDialog(false);
        setDeleteConfirmed(false);
      }
      return updated;
    });
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      newSet.delete(userId);
      return newSet;
    });
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
    setUserToDelete(null);
    setArtistNameToDelete("");
    setUsersToDelete([]);
    setDeleteConfirmed(false);
  };

  const handleManageMapArtist = (userId: number) => {
    setManageMenuUserId(null);
    navigate(`/artist-mapping?user=${userId}`);
  };

  const handleUnmapOpen = (user: UserWithArtists) => {
    const primaryArtist = user.artists?.find((a) => a.is_primary) || user.artists?.[0];
    if (!primaryArtist) return;
    setManageMenuUserId(null);
    setUnmapModal({ user, primaryArtist });
    setUnmapConfirmStep(1);
  };

  const handleUnmapClose = () => {
    setUnmapModal(null);
    setUnmapConfirmStep(1);
  };

  const handleUnmapConfirm = async () => {
    if (!unmapModal) return;
    if (unmapConfirmStep === 1) {
      setUnmapConfirmStep(2);
      return;
    }
    try {
      setUnmapLoading(true);
      await apiFetch("/admin/artist-unmap", {
        method: "POST",
        body: JSON.stringify({ user_id: unmapModal.user.id }),
      });
      toast.success("Artist unmapped successfully");
      setUnmapModal(null);
      setUnmapConfirmStep(1);
      setListRefreshKey((k) => k + 1);
    } catch (err: any) {
      toast.error(err?.message || "Failed to unmap artist");
    } finally {
      setUnmapLoading(false);
    }
  };

  const handleSyncOpen = (user: UserWithArtists) => {
    setManageMenuUserId(null);
    setSyncModalUser(user);
  };

  const handleSyncClose = () => setSyncModalUser(null);

  const handleSyncConfirm = async () => {
    if (!syncModalUser) return;
    try {
      setSyncLoading(true);
      await apiFetch("/admin/artist-sync", {
        method: "POST",
        body: JSON.stringify({ user_id: syncModalUser.id }),
      });
      toast.success("Sync triggered successfully");
      setSyncModalUser(null);
      setListRefreshKey((k) => k + 1);
    } catch (err: any) {
      toast.error(err?.message || "Failed to sync artist data");
    } finally {
      setSyncLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmed) {
      toast.error("Please confirm deletion by checking the checkbox");
      return;
    }

    const isBulkDelete = usersToDelete.length > 0;
    const usersToDeleteIds = isBulkDelete 
      ? usersToDelete.map(u => u.id)
      : userToDelete ? [userToDelete] : [];

    if (usersToDeleteIds.length === 0) {
      toast.error("No users selected for deletion");
      return;
    }

    try {
      setDeleteLoading(true);
      
      // Delete users in a single API call
      await apiFetch(`/admin/delete-user`, {
        method: "DELETE",
        body: JSON.stringify({
          userids: usersToDeleteIds
        }),
      });
      
      toast.success(isBulkDelete 
        ? `${usersToDeleteIds.length} user(s) deleted successfully`
        : "User deleted successfully"
      );
      
      // Close dialog first
      setShowDeleteDialog(false);
      setUserToDelete(null);
      setArtistNameToDelete("");
      setUsersToDelete([]);
      setSelectedUsers(new Set());
      setSelectionMode(false);
      setDeleteConfirmed(false);
      
      // Refresh the users list after dialog is closed
      setTableLoading(true);
      const filterParams: {
        artist_name?: string;
        email?: string;
        mobile?: string;
        fullName?: string;
        firstname?: string;
        lastname?: string;
      } = {};
      if (debouncedFilters.artistName) filterParams.artist_name = debouncedFilters.artistName;
      if (debouncedFilters.email) filterParams.email = debouncedFilters.email;
      if (debouncedFilters.mobile) filterParams.mobile = debouncedFilters.mobile;

      const tokens = normalizeFullNameTokens(debouncedFilters.fullName || "");
      if (tokens.length > 0) {
        if (tokens.length === 1) {
          filterParams.fullName = tokens[0];
        } else {
          filterParams.firstname = tokens[0];
          filterParams.lastname = tokens.slice(1).join(" ");
        }
      } else {
        // Backward compatibility
        if (debouncedFilters.firstname) filterParams.firstname = debouncedFilters.firstname;
        if (debouncedFilters.lastname) filterParams.lastname = debouncedFilters.lastname;
      }
      
      const response = await getAllUsers(currentPage, itemsPerPage, false, Object.keys(filterParams).length > 0 ? filterParams : undefined);
      
      // If current page is empty after deletion, go to previous page
      if (response.data.length === 0 && currentPage > 1) {
        const newPage = currentPage - 1;
        setCurrentPage(newPage);
        // Fetch again with the new page
        const newResponse = await getAllUsers(newPage, itemsPerPage, false, Object.keys(filterParams).length > 0 ? filterParams : undefined);
        setUsersData(newResponse.data);
        setPagination(newResponse.pagination);
      } else {
        setUsersData(response.data);
        setPagination(response.pagination);
      }
    } catch (error: any) {
      console.error("Error deleting user(s):", error);
      toast.error(error.message || "Failed to delete user(s)");
    } finally {
      setDeleteLoading(false);
      setTableLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-nova-dashboard-900 min-h-full md:min-h-screen">
        <div className="p-4 sm:p-6 md:p-8">
          <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern">Users List</span>
          <div className="flex items-center justify-center mt-[5rem]">
            <div className="text-gray-600 font-family-messina-sans">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-nova-dashboard-900 min-h-full md:min-h-screen">
        <div className="p-4 sm:p-6 md:p-8">
          <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern">Users List</span>
          <div className="flex items-center justify-center mt-[5rem]">
            <div className="text-red-600 font-family-messina-sans">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  // Format created_at to show only the date in MM/DD/YYYY format
  const formatDate = (dateString: string): string => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch {
      return "N/A";
    }
  };

  return (
    <div className="bg-nova-dashboard-900 min-h-full md:min-h-screen">
      {/* Main Content Area */}
      <div className="p-4 sm:p-6 md:p-8">
        {/* Header with Filter and Export Buttons (desktop sticky, mobile static) */}
        <div className="sticky top-0 z-30 isolate -mx-4 sm:-mx-6 md:-mx-8 px-4 sm:px-6 md:px-8 py-4 mt-0 mb-6 border-b border-gray-300 bg-[#F1F0EC] opacity-100 shadow-[0_1px_3px_rgba(0,0,0,0.06)] flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <span className="text-[1.875rem] leading-[1.1] font-regular text-gray-900 block font-family-messina-modern">Users List</span>
          <div className="w-full sm:w-auto flex items-center flex-wrap gap-2 sm:gap-3">
            {filteredAndSortedUsersData.length > 0 && (
              <div className="relative export-menu-container">
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  className="px-3 sm:px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-family-messina-sans flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Export</span>
                  <ChevronDown size={16} />
                </button>
                {showExportMenu && (
                  <div className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                    <button
                      onClick={exportToExcel}
                      className="w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors text-sm font-family-messina-sans flex items-center gap-2 text-gray-900"
                    >
                      <FileSpreadsheet size={16} />
                      <span>Export as Excel</span>
                    </button>
                    <button
                      onClick={exportToCSV}
                      className="w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors text-sm font-family-messina-sans flex items-center gap-2 text-gray-900 border-t border-gray-200"
                    >
                      <FileText size={16} />
                      <span>Export as CSV</span>
                    </button>
                    <button
                      onClick={exportArtistsSpotifyCSV}
                      className="w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors text-sm font-family-messina-sans flex items-center gap-2 text-gray-900 border-t border-gray-200"
                    >
                      <FileText size={16} />
                      <span>Artist + Spotify CSV</span>
                    </button>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={handleToggleSelectionMode}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm font-family-messina-sans ${
                selectionMode
                  ? "bg-gray-800 text-white hover:bg-gray-700"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              <CheckCircle size={16} />
              <span>{selectionMode ? "Cancel Selection" : "Select"}</span>
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm font-family-messina-sans ${
                showFilters ||
                filters.email ||
                filters.artistName ||
                filters.mobile ||
                filters.fullName
                  ? "bg-gray-800 text-white hover:bg-gray-700"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              <Filter size={16} />
              <span>Filter</span>
              {(filters.email || filters.artistName || filters.mobile || filters.fullName) && (
                <span className="ml-1 px-1.5 py-0.5 bg-white text-gray-800 rounded-full text-xs font-medium">
                  {[filters.email, filters.artistName, filters.mobile, filters.fullName].filter(Boolean).length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filter Section - Toggleable */}
        {showFilters && (
          <div className="mb-6 bg-white rounded-lg p-4 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-[1.125rem] font-regular text-gray-900 font-family-messina-modern">Filters</h3>
              <button
                onClick={() => setShowFilters(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
                aria-label="Close filters"
              >
                <X size={20} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Artist Name Filter */}
              <div>
                <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                  ARTIST
                </label>
                <input
                  type="text"
                  value={filters.artistName}
                  onChange={(e) => setFilters(prev => ({ ...prev, artistName: e.target.value }))}
                  placeholder="Filter by Artist Name"
                  className="w-full text-[0.875rem] text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans"
                />
              </div>

              {/* Email Filter */}
              <div>
                <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                  EMAIL
                </label>
                <input
                  type="text"
                  value={filters.email}
                  onChange={(e) => setFilters(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Filter by Email"
                  className="w-full text-[0.875rem] text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans"
                />
              </div>

              {/* Full Name Filter */}
              <div>
                <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                  FULL NAME
                </label>
                <input
                  type="text"
                  value={filters.fullName}
                  onChange={(e) => setFilters(prev => ({ ...prev, fullName: e.target.value }))}
                  placeholder="Filter by Full Name"
                  className="w-full text-[0.875rem] text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans"
                />
              </div>

              {/* Mobile Number Filter */}
              <div>
                <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                  PHONE NUMBER
                </label>
                <input
                  type="text"
                  value={filters.mobile}
                  onChange={(e) => setFilters(prev => ({ ...prev, mobile: e.target.value }))}
                  placeholder="Filter by Mobile Number"
                  className="w-full text-[0.875rem] text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans"
                />
              </div>
            </div>

            {/* Clear Filters Button */}
            {(filters.email || filters.artistName || filters.mobile || filters.fullName) && (
              <div className="mt-4">
                <button
                  onClick={() => setFilters({ email: "", artistName: "", fullName: "", firstname: "", lastname: "", mobile: "" })}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* Users List Table */}
        <div className="mb-6 sm:mb-8 mt-[2rem]">
          {selectedUsers.size > 0 && selectionMode && (
            <div className="mb-4 flex items-center justify-between">
              <div className="text-sm text-gray-600 font-family-messina-sans">
                {selectedUsers.size} user(s) selected
              </div>
              <button
                onClick={handleBulkDeleteClick}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-family-messina-sans flex items-center gap-2"
              >
                <Trash2 size={16} />
                <span>Delete Selected ({selectedUsers.size})</span>
              </button>
            </div>
          )}
          {usersData.length === 0 && !loading ? (
            <div className="text-center py-8">
              <div className="text-gray-600 font-family-messina-sans">No users data available</div>
            </div>
          ) : (
            <>
              <div className="relative overflow-x-auto min-h-[200px]">
                {tableLoading && <TableLoader />}
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-gray-300">
                      {selectionMode && (
                        <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedUsers.size > 0 && selectedUsers.size === currentPageData.length}
                              onChange={handleSelectAll}
                              className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                            />
                          </label>
                        </th>
                      )}
                      {/* <th 
                        className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('id')}
                      >
                        <div className="flex items-center gap-2">
                          <span>User ID</span>
                          {sortConfig.key === 'id' ? (
                            sortConfig.direction === 'asc' ? <ChevronUp size={12} className="text-gray-900" /> : <ChevronDown size={12} className="text-gray-900" />
                          ) : (
                            <ChevronUp size={12} className="text-gray-400 opacity-50" />
                          )}
                        </div>
                      </th>
                      <th 
                        className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('username')}
                      >
                        <div className="flex items-center gap-2">
                          <span>Username</span>
                          {sortConfig.key === 'username' ? (
                            sortConfig.direction === 'asc' ? <ChevronUp size={12} className="text-gray-900" /> : <ChevronDown size={12} className="text-gray-900" />
                          ) : (
                            <ChevronUp size={12} className="text-gray-400 opacity-50" />
                          )}
                        </div>
                      </th> */}
                      <th 
                        className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('artistName')}
                      >
                        <div className="flex items-center gap-2">
                          <span>Artist Name</span>
                          {sortConfig.key === 'artistName' ? (
                            sortConfig.direction === 'asc' ? <ChevronUp size={12} className="text-gray-900" /> : <ChevronDown size={12} className="text-gray-900" />
                          ) : (
                            <ChevronUp size={12} className="text-gray-400 opacity-50" />
                          )}
                        </div>
                      </th>
                      <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                        Full Name
                      </th>
                      <th 
                        className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('email')}
                      >
                        <div className="flex items-center gap-2">
                          <span>Email</span>
                          {sortConfig.key === 'email' ? (
                            sortConfig.direction === 'asc' ? <ChevronUp size={12} className="text-gray-900" /> : <ChevronDown size={12} className="text-gray-900" />
                          ) : (
                            <ChevronUp size={12} className="text-gray-400 opacity-50" />
                          )}
                        </div>
                      </th>
                      <th 
                        className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('mobile')}
                      >
                        <div className="flex items-center gap-2">
                      <span>Mobile Number</span>
                          {sortConfig.key === 'mobile' ? (
                            sortConfig.direction === 'asc' ? <ChevronUp size={12} className="text-gray-900" /> : <ChevronDown size={12} className="text-gray-900" />
                          ) : (
                            <ChevronUp size={12} className="text-gray-400 opacity-50" />
                          )}
                        </div>
                      </th>
                      <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">ChartMetric Id</th>
                      <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">Data Share Status</th>
                      <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">Manage</th>
                      <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPageData.map((user) => {
                      // Get primary artist name or first artist name
                      const primaryArtist = user.artists?.find(artist => artist.is_primary) || user.artists?.[0];
                      const isMapped = !!primaryArtist?.is_artist_mapped;
                      const artistDataStatus = primaryArtist?.artist_data_status ?? null;
                      const showSyncOption = artistDataStatus === "secondary_pending";
                      const artistName = primaryArtist?.artist_name || "N/A";
                      
                      return (
                        <tr key={user.id} className="border-b border-gray-200 hover:bg-gray-50">
                          {selectionMode && (
                            <td className="py-3 px-4">
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedUsers.has(user.id)}
                                  onChange={() => handleSelectUser(user.id, artistName)}
                                  className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                                />
                              </label>
                            </td>
                          )}
                          <td className="py-3 px-4 text-[0.875rem] text-gray-900 font-family-messina-sans">{artistName}</td>
                          <td className="py-3 px-4 text-[0.875rem] text-gray-900 font-family-messina-sans">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span>{`${user.firstname || ''} ${user.lastname || ''}`.trim() || 'N/A'}</span>
                              {(user.isSystemAdmin || user.systemRole === 'system_admin') && (
                                <span className="inline-flex items-center rounded-full bg-violet-100 text-violet-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                                  Admin
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-[0.875rem] text-gray-900 font-family-messina-sans">{user.email || "N/A"}</td>
                          <td className="py-3 px-4 text-[0.875rem] text-gray-900 font-family-messina-sans">{user.mobile || "N/A"}</td>
                          <td className="py-3 px-4 text-[0.875rem] text-gray-900 font-family-messina-sans">
                            {primaryArtist?.chartmetric_id || "N/A"}
                          </td>
                          <td className="py-3 px-4 text-[0.875rem] font-family-messina-sans">
                            {artistDataStatus === 'data_present' || artistDataStatus === 'primary_pending' ? (
                              <span className="text-green-600 font-medium">data present</span>
                            ) : (
                              <span className="text-orange-600 font-medium">no data</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-[0.875rem] text-gray-900 font-family-messina-sans">
                            <div className="flex items-center gap-2" data-manage-menu>
                              {isMapped ? (
                                <span className="inline-flex items-center gap-1 text-green-600 text-xs font-semibold">
                                  <CheckCircle className="h-4 w-4" />
                                  Mapped
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-orange-600 text-xs font-semibold">
                                  ⦿ Not mapped
                                </span>
                              )}
                              <div className="relative">
                                <button
                                  ref={manageMenuUserId === user.id ? manageButtonRef : undefined}
                                  type="button"
                                  onClick={() => setManageMenuUserId(manageMenuUserId === user.id ? null : user.id)}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors text-xs font-medium"
                                  title="Manage artist"
                                >
                                  <MoreVertical className="h-4 w-4" />
                                  Manage
                                </button>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-[0.875rem] text-gray-900 font-family-messina-sans">
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => navigate(`/user-profile/${user.id}`)}
                                className="flex items-center gap-1 text-gray-700 hover:text-gray-900 transition-colors"
                                title="View more details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              {/* <button
                                onClick={() => handleDeleteClick(user.id, artistName)}
                                className="flex items-center gap-1 text-red-600 hover:text-red-700 transition-colors"
                                title="Delete user"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button> */}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {(pagination || usersData.length > 0) && (
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                  {/* Items per page selector */}
                  <div className="flex items-center gap-2">
                    <span className="text-[0.875rem] text-gray-600 font-family-messina-sans">Items per page:</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="border border-gray-300 rounded px-2 py-1 text-[0.875rem] text-gray-900 font-family-messina-sans focus:outline-none focus:border-gray-900"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  {/* Page info */}
                  <div className="text-[0.875rem] text-gray-600 font-family-messina-sans">
                    {pagination ? (
                      <>
                        Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
                        {filteredAndSortedUsersData.length !== usersData.length && ` (${filteredAndSortedUsersData.length} shown after filtering)`}
                      </>
                    ) : (
                      <>
                        Showing {currentPageData.length} users
                      </>
                    )}
                  </div>

                  {/* Pagination buttons */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={!pagination?.hasPrevPage}
                      className={`p-2 rounded border ${
                        !pagination?.hasPrevPage
                          ? "border-gray-300 text-gray-400 cursor-not-allowed"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      } transition-colors`}
                      aria-label="Previous page"
                    >
                      <ChevronLeft size={16} />
                    </button>

                    {/* Page numbers */}
                    {totalPages > 1 && (
                      <div className="flex items-center gap-1">
                        {getPageNumbers().map((page, index) => {
                          if (page === '...') {
                            return (
                              <span key={`ellipsis-${index}`} className="px-2 text-gray-400">
                                ...
                              </span>
                            );
                          }

                          const pageNum = page as number;
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`px-3 py-1 rounded text-[0.875rem] font-family-messina-sans transition-colors ${
                                currentPage === pageNum
                                  ? "bg-gray-800 text-white"
                                  : "border border-gray-300 text-gray-700 hover:bg-gray-100"
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!pagination?.hasNextPage}
                      className={`p-2 rounded border ${
                        !pagination?.hasNextPage
                          ? "border-gray-300 text-gray-400 cursor-not-allowed"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      } transition-colors`}
                      aria-label="Next page"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Manage dropdown (portal so it's not clipped by table overflow) */}
      {manageUser && menuPosition &&
        createPortal(
          <div
            data-manage-dropdown
            className="fixed min-w-[160px] py-1 bg-white border border-gray-200 rounded-lg shadow-lg z-[9998]"
            style={{ top: menuPosition.top, left: menuPosition.left }}
          >
            <button
              type="button"
              onClick={() => handleManageMapArtist(manageUser.id)}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 font-family-messina-sans"
            >
              Map Artist
            </button>
            {!!(manageUser.artists?.find((a) => a.is_primary) || manageUser.artists?.[0])?.is_artist_mapped && (
              <button
                type="button"
                onClick={() => handleUnmapOpen(manageUser)}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 font-family-messina-sans"
              >
                UnMap Artist
              </button>
            )}
            {(manageUser.artists?.find((a) => a.is_primary) || manageUser.artists?.[0])?.artist_data_status === "secondary_pending" && (
              <button
                type="button"
                onClick={() => handleSyncOpen(manageUser)}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 font-family-messina-sans"
              >
                Sync Artist Data
              </button>
            )}
          </div>,
          document.body
        )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 font-family-messina-modern">
              {usersToDelete.length > 0 ? `Delete ${usersToDelete.length} User(s)` : "Delete User"}
            </h3>
            {usersToDelete.length > 0 ? (
              <div className="mb-4 max-h-60 overflow-y-auto">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 font-family-messina-sans">
                  Selected Artists
                </p>
                <div className="space-y-2">
                  {usersToDelete.map((user) => (
                    <div key={user.id} className="p-2 bg-gray-50 rounded border border-gray-200 flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-gray-900 font-family-messina-sans flex-1">
                        {user.artistName}
                      </p>
                      <button
                        onClick={() => handleRemoveUser(user.id)}
                        disabled={deleteLoading}
                        className={`flex items-center justify-center p-1 rounded hover:bg-gray-200 transition-colors ${
                          deleteLoading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        title="Remove from deletion list"
                      >
                        <X size={16} className="text-gray-600 hover:text-red-600" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : artistNameToDelete && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 font-family-messina-sans">
                  Artist Name
                </p>
                <p className="text-base font-semibold text-gray-900 font-family-messina-sans">
                  {artistNameToDelete}
                </p>
              </div>
            )}
            <p className="text-sm text-gray-600 mb-6 font-family-messina-sans">
              {usersToDelete.length > 0 
                ? `Are you sure you want to delete ${usersToDelete.length} user(s)? This action cannot be undone.`
                : "Are you sure you want to delete this user? This action cannot be undone."
              }
            </p>
            
            <div className="mb-6">
              <label className={`flex items-center gap-2 ${deleteLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  checked={deleteConfirmed}
                  onChange={(e) => setDeleteConfirmed(e.target.checked)}
                  disabled={deleteLoading}
                  className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900 disabled:cursor-not-allowed"
                />
                <span className="text-sm text-gray-700 font-family-messina-sans">
                  {usersToDelete.length > 0 
                    ? `I confirm that I want to delete ${usersToDelete.length} user(s)`
                    : "I confirm that I want to delete this user"
                  }
                </span>
              </label>
            </div>

            {deleteLoading && (
              <div className="mb-4 flex items-center justify-center gap-2 text-sm text-gray-600 font-family-messina-sans">
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                <span>
                  {usersToDelete.length > 0 
                    ? `Deleting ${usersToDelete.length} user(s)...`
                    : "Deleting user..."
                  }
                </span>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={handleDeleteCancel}
                disabled={deleteLoading}
                className={`px-4 py-2 bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-family-messina-sans ${
                  deleteLoading 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={!deleteConfirmed || deleteLoading}
                className={`px-4 py-2 rounded-lg transition-colors text-sm font-family-messina-sans flex items-center gap-2 ${
                  deleteConfirmed && !deleteLoading
                    ? "bg-red-600 text-white hover:bg-red-700"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                {deleteLoading && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* UnMap Artist modal */}
      {unmapModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 font-family-messina-modern">
              UnMap Artist
            </h3>
            {unmapConfirmStep === 1 ? (
              <>
                <div className="mb-4 space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide font-family-messina-sans">Artist</p>
                  <p className="text-base font-semibold text-gray-900 font-family-messina-sans">{unmapModal.primaryArtist.artist_name}</p>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide font-family-messina-sans mt-2">User</p>
                  <p className="text-sm text-gray-700 font-family-messina-sans">{unmapModal.user.email || `ID: ${unmapModal.user.id}`}</p>
                  {unmapModal.primaryArtist.chartmetric_id && unmapModal.primaryArtist.chartmetric_id !== "N/A" && (
                    <>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide font-family-messina-sans mt-2">ChartMetric Id</p>
                      <p className="text-sm text-gray-700 font-family-messina-sans">{unmapModal.primaryArtist.chartmetric_id}</p>
                    </>
                  )}
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleUnmapClose}
                    disabled={unmapLoading}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleUnmapConfirm}
                    disabled={unmapLoading}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-family-messina-sans"
                  >
                    UnMap
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-6 font-family-messina-sans">
                  This artist will be unmapped from the current user. Do you want to continue?
                </p>
                {unmapLoading && (
                  <div className="mb-4 flex items-center justify-center gap-2 text-sm text-gray-600 font-family-messina-sans">
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    <span>Unmapping...</span>
                  </div>
                )}
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setUnmapConfirmStep(1)}
                    disabled={unmapLoading}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleUnmapConfirm}
                    disabled={unmapLoading}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-family-messina-sans"
                  >
                    {unmapLoading ? "Unmapping..." : "Confirm UnMap"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Sync Artist Data modal */}
      {syncModalUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 font-family-messina-modern">
              Sync Artist Data
            </h3>
            <p className="text-sm text-gray-600 mb-6 font-family-messina-sans">
              Please make sure artist data is present in our databases before syncing.
            </p>
            {syncLoading && (
              <div className="mb-4 flex items-center justify-center gap-2 text-sm text-gray-600 font-family-messina-sans">
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                <span>Syncing...</span>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleSyncClose}
                disabled={syncLoading}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSyncConfirm}
                disabled={syncLoading}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-family-messina-sans"
              >
                {syncLoading ? "Syncing..." : "Sync"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersList;
